/**
 * KB-1645 — versioned, data-minimised AI-output marking and detection.
 *
 * This is an unsigned fallback profile, not C2PA and not proof of authorship or
 * legal compliance. A positive detector result means only "marker detected".
 * Keep this module client-safe: no Node globals or server/database imports.
 */

export const AI_OUTPUT_PROFILE = "urn:alchema:ai-output-marking:v1";
export const AI_OUTPUT_MARK_VERSION = 1;
export const AI_OUTPUT_MARK_KIND = "ai-assisted" as const;
export const AI_OUTPUT_MARK_PRODUCER = "Alchema";
export const AI_OUTPUT_MARK_PROVIDER = "Mistral";

const ENVELOPE_PREFIX = "A50M";
const TEXT_MAGIC = [0x41, 0x35, 0x30, 0x4d] as const; // A50M
const TEXT_KIND_AI_ASSISTED = 1;
const COMPACT_MARK_BYTES = 8;
const TEXT_MARK_LEADER = "\uFEFF";
const MARKDOWN_COMMENT_PREFIX = "<!-- alchema-ai-output-marking: ";
const MARKDOWN_COMMENT_SUFFIX = " -->";
const PDF_TEXT_LAYER_ATTRIBUTE = "data-alchema-ai-output-marking";
const TRANSPORT_BITS = COMPACT_MARK_BYTES * 8;

export type AiOutputMarkStatus =
  | "unmarked"
  | "marked"
  | "duplicate"
  | "malformed"
  | "unsupported-version"
  | "tampered";

export type AiOutputMarkLayer =
  | "envelope"
  | "text-watermark"
  | "transport"
  | "markdown-comment"
  | "pdf-metadata"
  | "pdf-text"
  | "docx-custom-property"
  | "docx-hidden-text";

export interface AiOutputMarkDetection {
  status: AiOutputMarkStatus;
  authenticity: "not-verified";
  version?: number;
  kind?: typeof AI_OUTPUT_MARK_KIND;
  count: number;
  layers: AiOutputMarkLayer[];
  reason?: string;
}

interface BuildEnvelopeOptions {
  /** Exposed for forward-compatibility tests. Production builders use v1. */
  version?: number;
}

function detection(
  status: AiOutputMarkStatus,
  options: Partial<Omit<AiOutputMarkDetection, "status" | "authenticity">> = {},
): AiOutputMarkDetection {
  return {
    status,
    authenticity: "not-verified",
    count: options.count ?? 0,
    layers: options.layers ?? [],
    ...options,
  };
}

function fnv1a32(input: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function compactChecksum(bytes: readonly number[]): number {
  let hash = 0x811c9dc5;
  for (const byte of bytes) {
    hash ^= byte;
    hash = Math.imul(hash, 0x01000193);
  }
  return hash & 0xffff;
}

function envelopeBase(version: number): string {
  return `${ENVELOPE_PREFIX}/${version};kind=${AI_OUTPUT_MARK_KIND};producer=${AI_OUTPUT_MARK_PRODUCER};provider=${AI_OUTPUT_MARK_PROVIDER}`;
}

/** Build the canonical deterministic ASCII envelope used by container formats. */
export function buildAiOutputEnvelope(options: BuildEnvelopeOptions = {}): string {
  const version = options.version ?? AI_OUTPUT_MARK_VERSION;
  if (!Number.isSafeInteger(version) || version < 0 || version > 255) {
    throw new RangeError("AI output marker version must be an integer from 0 to 255");
  }
  const base = envelopeBase(version);
  return `${base};integrity=${fnv1a32(base).toString(16).padStart(8, "0")}`;
}

const ENVELOPE_PATTERN = /A50M\/(\d+);kind=([^;\s<]+);producer=([^;\s<]+);provider=([^;\s<]+);integrity=([0-9a-fA-F]{8})/g;

/** Detect canonical ASCII envelopes in metadata or extracted artifact text. */
export function detectAiOutputEnvelope(value: string): AiOutputMarkDetection {
  const candidates = [...value.matchAll(ENVELOPE_PATTERN)];
  if (candidates.length === 0) {
    return value.includes(`${ENVELOPE_PREFIX}/`)
      ? detection("malformed", { reason: "AI output envelope is incomplete or syntactically invalid" })
      : detection("unmarked");
  }

  let marked = 0;
  let detectedVersion: number | undefined;
  for (const match of candidates) {
    const version = Number(match[1]);
    const kind = match[2];
    const producer = match[3];
    const provider = match[4];
    const suppliedIntegrity = match[5].toLowerCase();
    const actualBase = `${ENVELOPE_PREFIX}/${version};kind=${kind};producer=${producer};provider=${provider}`;
    const expectedIntegrity = fnv1a32(actualBase).toString(16).padStart(8, "0");

    if (suppliedIntegrity !== expectedIntegrity) {
      return detection("tampered", {
        count: candidates.length,
        reason: "AI output envelope integrity check failed",
      });
    }
    if (
      kind !== AI_OUTPUT_MARK_KIND
      || producer !== AI_OUTPUT_MARK_PRODUCER
      || provider !== AI_OUTPUT_MARK_PROVIDER
    ) {
      return detection("malformed", {
        count: candidates.length,
        reason: "AI output envelope contains unsupported canonical fields",
      });
    }
    if (version !== AI_OUTPUT_MARK_VERSION) {
      return detection("unsupported-version", {
        version,
        count: candidates.length,
        reason: `AI output marker version ${version} is not supported`,
      });
    }
    marked += 1;
    detectedVersion = version;
  }

  return detection(marked > 1 ? "duplicate" : "marked", {
    version: detectedVersion,
    kind: AI_OUTPUT_MARK_KIND,
    count: marked,
    layers: ["envelope"],
  });
}

function byteToVariationSelector(byte: number): string {
  if (byte < 0 || byte > 255 || !Number.isInteger(byte)) {
    throw new RangeError("Variation-selector byte must be from 0 to 255");
  }
  return String.fromCodePoint(byte <= 15 ? 0xfe00 + byte : 0xe0100 + byte - 16);
}

function variationSelectorToByte(codePoint: number): number | null {
  if (codePoint >= 0xfe00 && codePoint <= 0xfe0f) return codePoint - 0xfe00;
  if (codePoint >= 0xe0100 && codePoint <= 0xe01ef) return codePoint - 0xe0100 + 16;
  return null;
}

function buildCompactBytes(version = AI_OUTPUT_MARK_VERSION): number[] {
  if (!Number.isSafeInteger(version) || version < 0 || version > 255) {
    throw new RangeError("AI output marker version must be an integer from 0 to 255");
  }
  const body = [...TEXT_MAGIC, version, TEXT_KIND_AI_ASSISTED];
  const checksum = compactChecksum(body);
  return [...body, checksum >>> 8, checksum & 0xff];
}

/**
 * Build the compact copy/paste marker. Its byte-to-Variation-Selector mapping
 * follows C2PA 2.4 A.8, but A50M magic deliberately prevents false C2PA claims.
 */
export function buildAiTextMarker(options: BuildEnvelopeOptions = {}): string {
  return TEXT_MARK_LEADER
    + buildCompactBytes(options.version).map(byteToVariationSelector).join("");
}

interface CompactCandidate {
  start: number;
  end: number;
  bytes: number[];
}

function compactCandidates(value: string): CompactCandidate[] {
  const candidates: CompactCandidate[] = [];
  let offset = 0;
  while (offset < value.length) {
    const start = value.indexOf(TEXT_MARK_LEADER, offset);
    if (start < 0) break;
    const bytes: number[] = [];
    let cursor = start + TEXT_MARK_LEADER.length;
    while (cursor < value.length) {
      const codePoint = value.codePointAt(cursor);
      if (codePoint === undefined) break;
      const byte = variationSelectorToByte(codePoint);
      if (byte === null) break;
      bytes.push(byte);
      cursor += codePoint > 0xffff ? 2 : 1;
    }
    if (
      bytes.length >= TEXT_MAGIC.length
      && TEXT_MAGIC.every((byte, index) => bytes[index] === byte)
    ) {
      candidates.push({ start, end: cursor, bytes });
    }
    offset = Math.max(cursor, start + 1);
  }
  return candidates;
}

function detectCompactBytes(bytes: readonly number[]): AiOutputMarkDetection {
  if (bytes.length !== COMPACT_MARK_BYTES) {
    return detection("malformed", {
      reason: `Compact AI output marker must contain ${COMPACT_MARK_BYTES} bytes`,
    });
  }
  if (!TEXT_MAGIC.every((byte, index) => bytes[index] === byte)) {
    return detection("unmarked");
  }
  const version = bytes[4];
  const kind = bytes[5];
  const suppliedChecksum = (bytes[6] << 8) | bytes[7];
  if (suppliedChecksum !== compactChecksum(bytes.slice(0, 6))) {
    return detection("tampered", {
      version,
      reason: "Compact AI output marker integrity check failed",
    });
  }
  if (kind !== TEXT_KIND_AI_ASSISTED) {
    return detection("malformed", {
      version,
      reason: "Compact AI output marker kind is unsupported",
    });
  }
  if (version !== AI_OUTPUT_MARK_VERSION) {
    return detection("unsupported-version", {
      version,
      reason: `AI output marker version ${version} is not supported`,
    });
  }
  return detection("marked", {
    version,
    kind: AI_OUTPUT_MARK_KIND,
    count: 1,
    layers: ["text-watermark"],
  });
}

/** Detect the invisible marker in free-form text. */
export function detectAiTextMarker(value: string): AiOutputMarkDetection {
  const candidates = compactCandidates(value);
  if (candidates.length === 0) return detection("unmarked");
  if (candidates.some(({ bytes }) => bytes.length !== COMPACT_MARK_BYTES)) {
    return detection("malformed", {
      count: candidates.length,
      reason: "Compact AI output marker is truncated or has trailing selector bytes",
    });
  }

  for (const candidate of candidates) {
    const result = detectCompactBytes(candidate.bytes);
    if (result.status !== "marked") {
      return { ...result, count: candidates.length };
    }
  }

  return detection(candidates.length > 1 ? "duplicate" : "marked", {
    version: AI_OUTPUT_MARK_VERSION,
    kind: AI_OUTPUT_MARK_KIND,
    count: candidates.length,
    layers: ["text-watermark"],
  });
}

/** Remove only recognised A50M compact candidates, including malformed ones. */
export function stripAiTextMarkers(value: string): string {
  const candidates = compactCandidates(value);
  if (candidates.length === 0) return value;
  let result = "";
  let cursor = 0;
  for (const candidate of candidates) {
    result += value.slice(cursor, candidate.start);
    cursor = candidate.end;
  }
  return result + value.slice(cursor);
}

/** Prefix a single compact marker while preserving visible text byte-for-byte. */
export function markAiText(value: string): string {
  const existing = detectAiTextMarker(value);
  if (existing.status === "marked" && existing.count === 1) return value;
  return buildAiTextMarker() + stripAiTextMarkers(value);
}

/** Truncate visible content while keeping one complete marker inside maxLength. */
export function truncateMarkedAiText(value: string, maxLength: number): string {
  if (!Number.isSafeInteger(maxLength) || maxLength < 0) {
    throw new RangeError("maxLength must be a non-negative integer");
  }
  const marker = buildAiTextMarker();
  if (maxLength < marker.length) {
    throw new RangeError("maxLength is too small for the AI output marker");
  }
  const visible = stripAiTextMarkers(value);
  return marker + visible.slice(0, maxLength - marker.length);
}

function compactBytesToTransport(bytes: readonly number[]): string {
  return bytes
    .flatMap((byte) => Array.from({ length: 8 }, (_, bit) => (byte & (1 << (7 - bit))) === 0 ? " " : "\t"))
    .join("");
}

function transportRangeToCompactBytes(value: string, start: number): number[] {
  const bytes: number[] = [];
  for (let index = 0; index < TRANSPORT_BITS; index += 8) {
    let byte = 0;
    for (let bit = 0; bit < 8; bit += 1) {
      if (value[start + index + bit] === "\t") byte |= 1 << (7 - bit);
    }
    bytes.push(byte);
  }
  return bytes;
}

const TRANSPORT_MAGIC = compactBytesToTransport(TEXT_MAGIC);

export function buildAiTransportSuffix(options: BuildEnvelopeOptions = {}): string {
  return `\n${compactBytesToTransport(buildCompactBytes(options.version))}\n`;
}

/** Append a JSON-legal trailing-whitespace marker without changing parsed JSON. */
export function markAiTransportBody(value: string): string {
  const existing = detectAiTransportBody(value);
  if (existing.status === "marked" && existing.count === 1) return value;
  return value.trimEnd() + buildAiTransportSuffix();
}

export function detectAiTransportBody(value: string): AiOutputMarkDetection {
  let cursor = value.length;
  let count = 0;
  let firstError: AiOutputMarkDetection | undefined;

  while (cursor > 0) {
    // Canonical concatenation leaves empty separator lines. Skip them without
    // materialising an attacker-sized tail substring or array of lines.
    while (cursor > 0 && value[cursor - 1] === "\n") cursor -= 1;
    const lineEnd = cursor;
    while (cursor > 0 && (value[cursor - 1] === " " || value[cursor - 1] === "\t")) {
      cursor -= 1;
    }
    const lineStart = cursor;
    const lineLength = lineEnd - lineStart;
    const hasLineBoundary = lineStart === 0 || value[lineStart - 1] === "\n";
    if (
      lineLength < TRANSPORT_MAGIC.length
      || !hasLineBoundary
      || !value.startsWith(TRANSPORT_MAGIC, lineStart)
    ) {
      break;
    }

    count += 1;
    let result: AiOutputMarkDetection;
    if (lineLength !== TRANSPORT_BITS) {
      result = detection("malformed", {
        reason: `Transport AI output marker must contain ${TRANSPORT_BITS} bits`,
      });
    } else {
      result = detectCompactBytes(transportRangeToCompactBytes(value, lineStart));
    }
    // Scanning runs newest-to-oldest. Overwriting preserves the error from the
    // earliest candidate, matching forward detector precedence in fixed space.
    if (result.status !== "marked") firstError = result;
  }

  if (count === 0) return detection("unmarked");
  if (firstError) {
    return {
      ...firstError,
      count,
      layers: ["transport"],
    };
  }
  return detection(count > 1 ? "duplicate" : "marked", {
    version: AI_OUTPUT_MARK_VERSION,
    kind: AI_OUTPUT_MARK_KIND,
    count,
    layers: ["transport"],
  });
}

function appendHeaderToken(headers: Headers, name: string, value: string): void {
  const current = headers.get(name);
  if (!current) {
    headers.set(name, value);
  } else if (!current.includes(value)) {
    headers.set(name, `${current}, ${value}`);
  }
}

/**
 * Stream a marker after a successful JSON body. The original stream is not
 * buffered, preserving progressive delivery and ORPC's parsed payload shape.
 */
export function markSuccessfulAiJsonResponse(response: Response): Response {
  const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
  if (
    response.status < 200
    || response.status >= 300
    || !response.body
    || !contentType.includes("json")
  ) {
    return response;
  }

  const reader = response.body.getReader();
  const suffix = new TextEncoder().encode(buildAiTransportSuffix());
  let suffixSent = false;
  const body = new ReadableStream<Uint8Array>({
    async pull(controller) {
      const chunk = await reader.read();
      if (!chunk.done) {
        controller.enqueue(chunk.value);
        return;
      }
      if (!suffixSent) {
        controller.enqueue(suffix);
        suffixSent = true;
      }
      controller.close();
    },
    async cancel(reason) {
      await reader.cancel(reason);
    },
  });

  const headers = new Headers(response.headers);
  headers.delete("content-length");
  headers.set("Content-Profile", AI_OUTPUT_PROFILE);
  appendHeaderToken(headers, "Link", `<${AI_OUTPUT_PROFILE}>; rel="profile"`);

  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
}

export function markAiMarkdown(value: string): string {
  const result = detectAiMarkdown(value);
  if (result.status === "marked" && result.layers.length === 2) return value;
  const commentPattern = /<!-- alchema-ai-output-marking: [\s\S]*? -->\n?/g;
  const clean = stripAiTextMarkers(value.replace(commentPattern, ""));
  return `${MARKDOWN_COMMENT_PREFIX}${buildAiOutputEnvelope()}${MARKDOWN_COMMENT_SUFFIX}\n${clean}${buildAiTextMarker()}`;
}

export function detectAiMarkdown(value: string): AiOutputMarkDetection {
  const comments = [...value.matchAll(/<!-- alchema-ai-output-marking: ([\s\S]*?) -->/g)];
  const text = detectAiTextMarker(value);
  if (comments.length === 0 && text.status === "unmarked") return detection("unmarked");
  if (comments.length === 0) return text;

  const envelope = detectAiOutputEnvelope(comments.map((match) => match[1]).join("\n"));
  if (!["marked", "duplicate"].includes(envelope.status)) return envelope;
  if (!["marked", "duplicate"].includes(text.status)) return text;
  const count = Math.max(comments.length, text.count);
  return detection(comments.length > 1 || text.count > 1 ? "duplicate" : "marked", {
    version: AI_OUTPUT_MARK_VERSION,
    kind: AI_OUTPUT_MARK_KIND,
    count,
    layers: ["markdown-comment", "text-watermark"],
  });
}

/** Add static PDF metadata and imperceptible text layers to printable HTML. */
export function markAiPrintHtml(html: string): string {
  const envelope = buildAiOutputEnvelope();
  let output = html;

  const titleMatch = output.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  const titleDetection = detectAiOutputEnvelope(titleMatch?.[1] ?? "");
  if (!titleMatch) {
    if (/<head[^>]*>/i.test(output)) {
      output = output.replace(/<head[^>]*>/i, (head) => `${head}<title>${envelope}</title>`);
    } else {
      output = `<head><title>${envelope}</title></head>${output}`;
    }
  } else if (titleDetection.status !== "marked") {
    output = output.replace(/(<title[^>]*>)([\s\S]*?)(<\/title>)/i, `$1$2 — ${envelope}$3`);
  }

  if (!output.includes(PDF_TEXT_LAYER_ATTRIBUTE)) {
    const layer = `<span ${PDF_TEXT_LAYER_ATTRIBUTE}="v1" aria-hidden="true" style="position:fixed;left:0;bottom:0;font-size:.1px;line-height:1;opacity:.01;color:transparent">${envelope}</span>`;
    output = /<\/body>/i.test(output)
      ? output.replace(/<\/body>/i, `${layer}</body>`)
      : output + layer;
  }
  return output;
}

export function detectAiPdfEvidence(input: {
  title?: string | null;
  text?: string | null;
}): AiOutputMarkDetection {
  const metadata = detectAiOutputEnvelope(input.title ?? "");
  const text = detectAiOutputEnvelope(input.text ?? "");
  if (metadata.status === "unmarked" && text.status === "unmarked") return detection("unmarked");
  for (const result of [metadata, text]) {
    if (!["unmarked", "marked", "duplicate"].includes(result.status)) return result;
  }
  const layers: AiOutputMarkLayer[] = [];
  if (metadata.status !== "unmarked") layers.push("pdf-metadata");
  if (text.status !== "unmarked") layers.push("pdf-text");
  const count = metadata.count + text.count;
  if (layers.length !== 2) {
    return detection("malformed", {
      version: AI_OUTPUT_MARK_VERSION,
      kind: AI_OUTPUT_MARK_KIND,
      count,
      layers,
      reason: "PDF AI output marking requires metadata and text layers",
    });
  }
  return detection(metadata.status === "duplicate" || text.status === "duplicate" ? "duplicate" : "marked", {
    version: AI_OUTPUT_MARK_VERSION,
    kind: AI_OUTPUT_MARK_KIND,
    count,
    layers,
  });
}

export function detectAiDocxParts(input: {
  customPropertiesXml?: string | null;
  documentXml?: string | null;
}): AiOutputMarkDetection {
  const custom = detectAiOutputEnvelope(input.customPropertiesXml ?? "");
  const document = detectAiOutputEnvelope(input.documentXml ?? "");
  if (custom.status === "unmarked" && document.status === "unmarked") return detection("unmarked");
  for (const result of [custom, document]) {
    if (!["unmarked", "marked", "duplicate"].includes(result.status)) return result;
  }
  const layers: AiOutputMarkLayer[] = [];
  if (custom.status !== "unmarked") layers.push("docx-custom-property");
  if (document.status !== "unmarked") layers.push("docx-hidden-text");
  const count = custom.count + document.count;
  if (layers.length !== 2) {
    return detection("malformed", {
      version: AI_OUTPUT_MARK_VERSION,
      kind: AI_OUTPUT_MARK_KIND,
      count,
      layers,
      reason: "DOCX AI output marking requires custom-property and hidden-text layers",
    });
  }
  return detection(custom.status === "duplicate" || document.status === "duplicate" ? "duplicate" : "marked", {
    version: AI_OUTPUT_MARK_VERSION,
    kind: AI_OUTPUT_MARK_KIND,
    count,
    layers,
  });
}
